/**
 * F.1c (GEN-2999) — selection-source migration.
 *
 * `TestOptions.selectedComparatorRelationships` is the source of truth for
 * which family-member comparators an order includes (design doc §9.2 / §9.6).
 * The legacy `biologicalMother` / `biologicalFather` boolean pair was retired
 * in P12 of the MOTHER/FATHER migration; consumers must read with
 * {@link selectionHasRelationship} and write with
 * {@link withRelationshipSelection} / {@link withRelationshipSelections}
 * rather than touching the array directly.
 *
 * Only `MOTHER` / `FATHER` come from the legacy seed paths — other
 * relationships (twins, guardians, distant relatives) are added through the
 * comparator UI after the session exists.
 */
import type { TestOptions } from '@/components/order/OrderTypes'
import type { FamilyMemberRelationship } from '@/ordering/canonicalOrder'

/** True when `relationship` is currently selected on the test options. */
export function selectionHasRelationship(
  options: Pick<TestOptions, 'selectedComparatorRelationships'>,
  relationship: FamilyMemberRelationship
): boolean {
  const list = options.selectedComparatorRelationships
  if (!Array.isArray(list) || list.length === 0) return false
  return list.includes(relationship)
}

/**
 * Return a copy of `options` with `relationship` added (when `present` is
 * true) or removed (when `present` is false). Preserves the existing order of
 * any other relationships. No-op when the desired state already holds.
 */
export function withRelationshipSelection(
  options: TestOptions,
  relationship: FamilyMemberRelationship,
  present: boolean
): TestOptions {
  const current = options.selectedComparatorRelationships ?? []
  const isPresent = current.includes(relationship)
  if (present === isPresent) return options
  const next: FamilyMemberRelationship[] = present
    ? [...current, relationship]
    : current.filter((r) => r !== relationship)
  return { ...options, selectedComparatorRelationships: next }
}

/**
 * Bulk relationship-presence update: applies each `{ relationship, present }`
 * entry in iteration order. Use for rehydration paths that overwrite multiple
 * relationships at once (e.g. canonical-derived MOTHER + FATHER selection on
 * remount); single-side toggles should prefer
 * {@link withRelationshipSelection}.
 */
export function withRelationshipSelections(
  options: TestOptions,
  updates: Partial<Record<FamilyMemberRelationship, boolean>>
): TestOptions {
  let next = options
  for (const [relationship, present] of Object.entries(updates) as Array<
    [FamilyMemberRelationship, boolean | undefined]
  >) {
    if (typeof present !== 'boolean') continue
    next = withRelationshipSelection(next, relationship, present)
  }
  return next
}
